/* eslint-disable no-redeclare */
/* eslint-disable @typescript-eslint/no-shadow */
/* eslint-disable default-param-last */
import {
  areManualDocumentPostingsReadable,
  CustomSubscriptionType,
  INVOICE_TABLE_NAME,
  InvoiceStatus,
  InvoiceType,
  addInvoiceIntegrationsProperties,
  addInvoiceSentVersionChannels,
  chargeInvoiceByMandate as chargeInvoiceByMandateMutation,
  cleanInputCreate,
  cleanInputUpdate,
  createInvoice as createMutation,
  deleteDraftInvoice as deleteDraftInvoiceMutation,
  deleteInvoice as deleteMutation,
  duplicateCompanyInvoice as duplicateCompanyInvoiceMutation,
  getDateTimeDifference,
  getInvoice as getQuery,
  getReadId,
  getTableClientId,
  liftInvoiceDispute as liftInvoiceDisputeMutation,
  openInvoiceDispute as openInvoiceDisputeMutation,
  getTypedCustomSubscriptionValueForRefetch,
  isCommunicationAwaitingManualSend,
  isIdInList,
  isNilOrEmpty,
  onCreateCustomSubscription,
  onCreateInvoice,
  onCreatePosting,
  onDeleteCustomSubscription,
  onDeleteInvoice,
  onDeletePosting,
  onUpdateCustomSubscription,
  onUpdateInvoice,
  onUpdatePosting,
  resendInvoiceToPeppol as resendInvoiceToPeppolMutation,
  resetInvoiceToDraft as resetInvoiceToDraftMutation,
  reverseCompanyInvoice as reverseCompanyInvoiceMutation,
  sendInvoice as sendInvoiceMutation,
  syncInvoices,
  todayDateTime,
  updateInvoice as updateMutation,
  validateInvoice as validateInvoiceMutation,
  type ChargeInvoiceByMandateMutation,
  type ChargeInvoiceByMandateMutationVariables,
  CompanyInvoiceSendMethod,
  type CreateInvoiceInput,
  type CreateInvoiceMutationVariables,
  type CustomSubscription,
  type DateTime,
  type DeleteDraftInvoiceMutationVariables,
  type DeleteInvoiceMutationVariables,
  type DuplicateCompanyInvoiceMutationVariables,
  type Invoice,
  type LiftInvoiceDisputeInput,
  type LiftInvoiceDisputeMutationVariables,
  type OpenInvoiceDisputeInput,
  type OpenInvoiceDisputeMutationVariables,
  type LooseObject,
  type MutationStatus,
  type OnCreateCustomSubscriptionSubscription,
  type OnCreateInvoiceSubscription,
  type OnCreatePostingSubscription,
  type OnDeleteCustomSubscriptionSubscription,
  type OnDeleteInvoiceSubscription,
  type OnDeletePostingSubscription,
  type OnUpdateCustomSubscriptionSubscription,
  type OnUpdateInvoiceSubscription,
  type OnUpdatePostingSubscription,
  type Posting,
  type ResendInvoiceToPeppolMutationVariables,
  type ResetInvoiceToDraftMutationVariables,
  type ReverseCompanyInvoiceMutationVariables,
  type SendInvoiceMutationVariables,
  type StatusResult,
  type UpdateInvoiceInput,
  type ValidateInvoiceMutationVariables,
} from '@rentguru/commons-utils';
import {
  isTooEarlyToRetryFailedFetch,
  MILLISECONDS_BEFORE_RETRYING_A_FAILED_FETCH,
  NUMBER_OF_MINUTES_FOR_REFETCH,
  deleteEntityWithFetchBefore,
  get,
  list,
  mutation,
  useSubscriptions,
} from '@up2rent/graphql';
import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';
import { createContext, useContext, useEffect, useMemo, useReducer, useRef, type ReactNode, type Reducer } from 'react';

import { fetchUntil, retryWhileDocumentNotReady } from 'src/components/ui/InvoiceLifecycle/invoiceLifecycleUtils';

import { FetchByType, fetchCommunication } from './CommunicationsContext';
import { useContextLoader } from './ContextLoader';
import { useLeases } from './LeasesContext';
import { fetchPostings, usePostings } from './PostingsContext';
import { useUser } from './UserContext';
import {
  getInvoiceType,
  listInvoicesForLatePaymentFilter,
  syncInvoices as syncInvoicesForFolder,
} from './utils/cleanedQueries';
import { usePermissions } from './utils/PermissionsContext';

export interface SendInvoiceResult {
  communicationId?: string;
  awaitingManualSend: boolean;
}

export interface InvoiceContext
  extends Omit<State, 'invoices' | 'companyInvoiceIdsAtFetchStart' | 'companyInvoiceIdsDeletedDuringFetch'> {
  createInvoice: (input: CreateInvoiceInput | Omit<CreateInvoiceInput, 'clientId' | 'readId'>) => Promise<Invoice>;
  resendInvoiceToPeppol: (invoiceId: string) => Promise<void>;
  chargeInvoiceByMandate: (invoiceId: string) => Promise<void>;
  getCompanyInvoices: () => Invoice[];
  refetchCompanyInvoices: () => void;
  getUnpaidRentInvoices: () => Invoice[];
  getInvoicesOfLease: (leaseId: string) => Invoice[];
  getUnpaidInvoicesOfLease: (leaseId: string) => Invoice[];
  getInvoicesOfOwner: (ownerId?: string) => Promise<Invoice[]>;
  getTenantStatementsInvoices: () => Invoice[];
  updateInvoice: (updates: UpdateInvoiceInput) => Promise<Invoice>;
  openInvoiceDispute: (input: OpenInvoiceDisputeInput, leaseId?: string | null) => Promise<boolean>;
  liftInvoiceDispute: (input: LiftInvoiceDisputeInput, leaseId?: string | null) => Promise<boolean>;
  deleteInvoice: (invoice: Invoice, refetch?: boolean) => Promise<Invoice | null>;
  deleteDraftInvoice: (invoiceId: string) => Promise<void>;
  validateInvoice: (invoiceId: string, invoiceNumber?: string | null, renumber?: boolean) => Promise<MutationStatus>;
  duplicateCompanyInvoice: (invoiceId: string) => Promise<string>;
  reverseCompanyInvoice: (invoiceId: string) => Promise<void>;
  resetInvoiceToDraft: (invoiceId: string, reason?: string | null) => Promise<void>;
  sendInvoice: (
    invoiceId: string,
    sendMethods?: CompanyInvoiceSendMethod[],
    note?: string
  ) => Promise<SendInvoiceResult>;
  invoices: Invoice[] | null;
}

export interface State {
  loading: boolean;
  error: string | null;
  shouldFetchTenantStatements: boolean;
  shouldFetchLeases: string[];
  leaseRefetchesPending: string[];
  shouldFetchOwners: string[];
  shouldFetchCompany: boolean;
  companyRefetchPending: boolean;
  shouldRefetchInvoices: boolean;
  lastTenantStatementsFetch: DateTime | null;
  lastLeasesFetch: { [leaseId: string]: DateTime | null };
  lastLeasesFetchStartedAt: { [leaseId: string]: DateTime };
  lastOwnersFetch: { [ownerId: string]: DateTime | null };
  lastFetchFailure?: DateTime;
  companyInvoicesFetched: boolean;
  companyInvoicesError: string | null;
  shouldFetchUnpaidRentInvoices: boolean;
  unpaidRentInvoicesFetched: boolean;
  unpaidRentInvoicesError: string | null;
  unpaidRentInvoices: Invoice[] | null;
  companyInvoiceIdsAtFetchStart: string[];
  companyInvoiceIdsDeletedDuringFetch: string[];
  invoices: Invoice[] | null;
  validatingInvoiceIds: string[];
}

export enum InvoiceActionType {
  ADD = 'ADD',
  UPDATE = 'UPDATE',
  UPDATE_STATUS = 'UPDATE_STATUS',
  DELETE = 'DELETE',
  FETCH_LEASE = 'FETCH_LEASE',
  FETCH_OWNER = 'FETCH_OWNER',
  FETCH_COMPANY = 'FETCH_COMPANY',
  FETCH_UNPAID_RENT_INVOICES = 'FETCH_UNPAID_RENT_INVOICES',
  FETCHED_UNPAID_RENT_INVOICES = 'FETCHED_UNPAID_RENT_INVOICES',
  FETCH_UNPAID_RENT_INVOICES_ERROR = 'FETCH_UNPAID_RENT_INVOICES_ERROR',
  FETCH_TENANT_STATEMENTS = 'FETCH_TENANT_STATEMENTS',
  FETCHED = 'FETCHED',
  FETCHED_COMPANY = 'FETCHED_COMPANY',
  FETCH_COMPANY_ERROR = 'FETCH_COMPANY_ERROR',
  FETCH_FAILED = 'FETCH_FAILED',
  FAILURE_BACKOFF_ELAPSED = 'FAILURE_BACKOFF_ELAPSED',
  REFETCH = 'REFETCH',
  SET_VALIDATING = 'SET_VALIDATING',
  UNSET_VALIDATING = 'UNSET_VALIDATING',
}

export type Action =
  | {
      type: InvoiceActionType.FETCH_LEASE;
      payload: { leaseId: string; force?: boolean };
    }
  | {
      type: InvoiceActionType.FETCH_OWNER;
      payload: { ownerId?: string; force?: boolean };
    }
  | {
      type: InvoiceActionType.FETCH_UNPAID_RENT_INVOICES;
    }
  | {
      type: InvoiceActionType.FETCHED_UNPAID_RENT_INVOICES;
      payload: { invoices: Invoice[] };
    }
  | {
      type: InvoiceActionType.FETCH_UNPAID_RENT_INVOICES_ERROR;
      payload: { error: string };
    }
  | {
      type: InvoiceActionType.FETCH_COMPANY;
      payload: { force?: boolean };
    }
  | {
      type: InvoiceActionType.FETCH_TENANT_STATEMENTS | InvoiceActionType.REFETCH;
    }
  | {
      type: InvoiceActionType.FETCHED;
      payload: { invoices: Invoice[]; leaseId?: string; ownerId?: string; fetchStartedAt?: DateTime };
    }
  | {
      type: InvoiceActionType.FETCHED_COMPANY;
      payload: { invoices: Invoice[] };
    }
  | {
      type: InvoiceActionType.FETCH_COMPANY_ERROR;
      payload: { error: string };
    }
  | {
      type: InvoiceActionType.FETCH_FAILED;
      payload: { leaseId?: string; ownerId?: string };
    }
  | {
      type: InvoiceActionType.FAILURE_BACKOFF_ELAPSED;
    }
  | {
      type: InvoiceActionType.ADD | InvoiceActionType.UPDATE;
      payload: { invoice: Invoice };
    }
  | {
      type: InvoiceActionType.UPDATE_STATUS;
      payload: { id: string; status: InvoiceStatus };
    }
  | {
      type: InvoiceActionType.DELETE;
      payload: { id: string };
    }
  | {
      type: InvoiceActionType.SET_VALIDATING | InvoiceActionType.UNSET_VALIDATING;
      payload: { invoiceId: string };
    };

export const initialState: State = {
  loading: false,
  error: null,
  shouldFetchTenantStatements: false,
  shouldFetchCompany: false,
  companyRefetchPending: false,
  shouldRefetchInvoices: false,
  shouldFetchLeases: [],
  leaseRefetchesPending: [],
  shouldFetchOwners: [],

  lastTenantStatementsFetch: null,
  lastLeasesFetch: {},
  lastLeasesFetchStartedAt: {},
  lastOwnersFetch: {},
  companyInvoicesFetched: false,
  companyInvoicesError: null,
  shouldFetchUnpaidRentInvoices: false,
  unpaidRentInvoicesFetched: false,
  unpaidRentInvoicesError: null,
  unpaidRentInvoices: null,
  companyInvoiceIdsAtFetchStart: [],
  companyInvoiceIdsDeletedDuringFetch: [],
  invoices: null,
  validatingInvoiceIds: [],
};

const isUnpaidRentInvoice = (invoice: Invoice): boolean => invoice.type === InvoiceType.RENT && !invoice.paid;

export const updateUnpaidRentSnapshot = (snapshot: Invoice[] | null, invoice: Invoice): Invoice[] | null => {
  if (isNil(snapshot)) {
    return snapshot;
  }
  const withoutInvoice = snapshot.filter((item) => item.id !== invoice.id);
  return isUnpaidRentInvoice(invoice) ? [...withoutInvoice, invoice] : withoutInvoice;
};

export const contextReducer = (state: State, action: Action): State => {
  switch (action.type) {
    case InvoiceActionType.FETCH_LEASE:
      // Check depending on param if we need to fetch
      const actionLeaseId = action.payload.leaseId;
      const lastLeaseIdFetch = state.lastLeasesFetch[actionLeaseId];
      const actionForce = action.payload.force;
      if (
        actionForce &&
        state.shouldFetchLeases.includes(actionLeaseId) &&
        !state.leaseRefetchesPending.includes(actionLeaseId)
      ) {
        return { ...state, leaseRefetchesPending: [...state.leaseRefetchesPending, actionLeaseId] };
      }
      if (
        !actionLeaseId ||
        state.shouldFetchLeases.includes(actionLeaseId) ||
        (!actionForce && isTooEarlyToRetryFailedFetch(state.lastFetchFailure)) ||
        (!actionForce &&
          lastLeaseIdFetch &&
          getDateTimeDifference({ start: lastLeaseIdFetch, end: todayDateTime() }, 'minutes') <
            NUMBER_OF_MINUTES_FOR_REFETCH)
      ) {
        return state;
      }
      return {
        ...state,
        shouldFetchLeases: [...state.shouldFetchLeases, actionLeaseId],
        loading: true,
      };
    case InvoiceActionType.FETCH_OWNER:
      // Check depending on param if we need to fetch
      const actionOwnerId = action.payload.ownerId;
      const actionOwnerForce = action.payload.force;
      const lastOwnerIdFetch = actionOwnerId ? state.lastOwnersFetch[actionOwnerId] : undefined;
      if (
        state.loading ||
        (actionOwnerId && state.shouldFetchOwners.includes(actionOwnerId)) ||
        (!actionOwnerForce && isTooEarlyToRetryFailedFetch(state.lastFetchFailure)) ||
        (!actionOwnerForce &&
          lastOwnerIdFetch &&
          getDateTimeDifference({ start: lastOwnerIdFetch, end: todayDateTime() }, 'minutes') <
            NUMBER_OF_MINUTES_FOR_REFETCH)
      ) {
        return state;
      }
      return {
        ...state,
        shouldFetchOwners: actionOwnerId ? [...state.shouldFetchOwners, actionOwnerId] : state.shouldFetchOwners,
        loading: true,
      };
    case InvoiceActionType.FETCH_UNPAID_RENT_INVOICES:
      if (state.shouldFetchUnpaidRentInvoices || state.unpaidRentInvoicesFetched) {
        return state;
      }
      return { ...state, shouldFetchUnpaidRentInvoices: true, unpaidRentInvoicesError: null };
    case InvoiceActionType.FETCHED_UNPAID_RENT_INVOICES:
      return {
        ...state,
        shouldFetchUnpaidRentInvoices: false,
        unpaidRentInvoicesFetched: true,
        unpaidRentInvoicesError: null,
        unpaidRentInvoices: action.payload.invoices,
      };
    case InvoiceActionType.FETCH_UNPAID_RENT_INVOICES_ERROR:
      return {
        ...state,
        shouldFetchUnpaidRentInvoices: false,
        unpaidRentInvoicesFetched: false,
        unpaidRentInvoicesError: action.payload.error,
      };
    case InvoiceActionType.FETCH_COMPANY:
      if (state.shouldFetchCompany) {
        return action.payload.force && !state.companyRefetchPending ? { ...state, companyRefetchPending: true } : state;
      }
      if (!action.payload.force && (state.companyInvoicesFetched || !isNil(state.companyInvoicesError))) {
        return state;
      }
      return {
        ...state,
        shouldFetchCompany: true,
        companyRefetchPending: false,
        companyInvoicesError: null,
        loading: true,
        companyInvoiceIdsAtFetchStart: (state.invoices ?? [])
          .filter((invoice) => invoice.type === InvoiceType.COMPANY)
          .map((invoice) => invoice.id),
        companyInvoiceIdsDeletedDuringFetch: [],
      };
    case InvoiceActionType.FETCH_COMPANY_ERROR:
      return {
        ...state,
        loading: false,
        shouldFetchCompany: false,
        companyInvoiceIdsAtFetchStart: [],
        companyInvoiceIdsDeletedDuringFetch: [],
        companyInvoicesError: action.payload.error,
      };
    case InvoiceActionType.FETCHED_COMPANY: {
      const fetchedCompanyInvoiceById = new Map(action.payload.invoices.map((invoice) => [invoice.id, invoice]));
      const knownAtFetchStart = new Set(state.companyInvoiceIdsAtFetchStart);
      const oldInvoices = state.invoices ?? [];
      const survivingOldCompanyInvoices = oldInvoices.filter(
        (oldInvoice) =>
          oldInvoice.type === InvoiceType.COMPANY &&
          !fetchedCompanyInvoiceById.has(oldInvoice.id) &&
          !knownAtFetchStart.has(oldInvoice.id)
      );
      const oldCompanyInvoiceById = new Map(
        oldInvoices.filter((invoice) => invoice.type === InvoiceType.COMPANY).map((invoice) => [invoice.id, invoice])
      );
      const deletedDuringFetch = new Set(state.companyInvoiceIdsDeletedDuringFetch);
      const reconciledFetchedInvoices = action.payload.invoices
        .filter((fetchedInvoice) => !deletedDuringFetch.has(fetchedInvoice.id))
        .map((fetchedInvoice) => {
          const oldInvoice = oldCompanyInvoiceById.get(fetchedInvoice.id);
          return !isNil(oldInvoice) && (oldInvoice._version ?? 0) > (fetchedInvoice._version ?? 0)
            ? oldInvoice
            : fetchedInvoice;
        });
      const mergedCompanyInvoices = [
        ...reconciledFetchedInvoices,
        ...survivingOldCompanyInvoices,
        ...oldInvoices.filter((oldInvoice) => oldInvoice.type !== InvoiceType.COMPANY),
      ];
      const numberedInvoiceIds = new Set(
        action.payload.invoices.filter((invoice) => invoice.invoiceNumber).map((invoice) => invoice.id)
      );
      return {
        ...state,
        loading: false,
        companyInvoicesFetched: true,
        companyInvoicesError: null,
        shouldFetchCompany: false,
        companyInvoiceIdsAtFetchStart: [],
        companyInvoiceIdsDeletedDuringFetch: [],
        invoices: mergedCompanyInvoices,
        validatingInvoiceIds: state.validatingInvoiceIds.filter((id) => !numberedInvoiceIds.has(id)),
      };
    }
    case InvoiceActionType.FETCH_TENANT_STATEMENTS:
      // Check lastFetch
      if (
        state.loading ||
        state.shouldFetchTenantStatements ||
        (state.lastTenantStatementsFetch &&
          getDateTimeDifference({ start: state.lastTenantStatementsFetch, end: todayDateTime() }, 'minutes') <
            NUMBER_OF_MINUTES_FOR_REFETCH)
      ) {
        return state;
      }
      return {
        ...state,
        shouldFetchTenantStatements: true,
        loading: true,
      };
    case InvoiceActionType.FETCHED:
      // Build List with merged object
      const leaseId = action.payload.leaseId;
      const ownerId = action.payload.ownerId;
      const newInvoices = [
        ...action.payload.invoices,
        ...(state.invoices ?? []).filter(
          (oldInvoice) => !action.payload.invoices.some((newInvoice) => newInvoice.id === oldInvoice.id)
        ),
      ];
      if (leaseId) {
        const isRefetchPending = state.leaseRefetchesPending.includes(leaseId);
        const newShouldFetchLeases = [
          ...state.shouldFetchLeases.filter((leaseIdToFetch) => leaseIdToFetch !== leaseId),
          ...(isRefetchPending ? [leaseId] : []),
        ];
        return {
          ...state,
          loading: !isEmpty(newShouldFetchLeases) || state.shouldFetchTenantStatements,
          shouldFetchLeases: newShouldFetchLeases,
          leaseRefetchesPending: state.leaseRefetchesPending.filter((pendingLeaseId) => pendingLeaseId !== leaseId),
          shouldRefetchInvoices: false,
          lastLeasesFetch: { ...state.lastLeasesFetch, [leaseId]: todayDateTime() },
          lastLeasesFetchStartedAt: isNil(action.payload.fetchStartedAt)
            ? state.lastLeasesFetchStartedAt
            : { ...state.lastLeasesFetchStartedAt, [leaseId]: action.payload.fetchStartedAt },
          invoices: newInvoices,
        };
      }
      if (ownerId) {
        const newShouldFetchOwners = state.shouldFetchOwners.filter((ownerIdToFetch) => ownerIdToFetch !== ownerId);
        return {
          ...state,
          loading: !isEmpty(newShouldFetchOwners) || state.shouldFetchTenantStatements,
          shouldFetchOwners: newShouldFetchOwners,
          shouldRefetchInvoices: false,
          lastOwnersFetch: { ...state.lastOwnersFetch, [ownerId]: todayDateTime() },
          invoices: newInvoices,
        };
      }
      // Tenants Statement
      return {
        ...state,
        loading: !isEmpty(state.shouldFetchLeases),
        shouldFetchTenantStatements: false,
        shouldRefetchInvoices: false,
        lastTenantStatementsFetch: todayDateTime(),
        invoices: newInvoices,
      };
    case InvoiceActionType.FETCH_FAILED: {
      const failedLeaseId = action.payload.leaseId;
      if (failedLeaseId) {
        const remainingLeasesToFetch = state.shouldFetchLeases.filter(
          (leaseIdToFetch) => leaseIdToFetch !== failedLeaseId
        );
        return {
          ...state,
          loading: !isEmpty(remainingLeasesToFetch) || state.shouldFetchTenantStatements,
          shouldFetchLeases: remainingLeasesToFetch,
          leaseRefetchesPending: state.leaseRefetchesPending.filter(
            (pendingLeaseId) => pendingLeaseId !== failedLeaseId
          ),
          shouldRefetchInvoices: false,
          lastFetchFailure: todayDateTime(),
        };
      }
      const failedOwnerId = action.payload.ownerId;
      if (failedOwnerId) {
        const remainingOwnersToFetch = state.shouldFetchOwners.filter(
          (ownerIdToFetch) => ownerIdToFetch !== failedOwnerId
        );
        return {
          ...state,
          loading: !isEmpty(remainingOwnersToFetch) || state.shouldFetchTenantStatements,
          shouldFetchOwners: remainingOwnersToFetch,
          shouldRefetchInvoices: false,
          lastFetchFailure: todayDateTime(),
        };
      }
      return {
        ...state,
        loading: !isEmpty(state.shouldFetchLeases),
        shouldFetchTenantStatements: false,
        shouldRefetchInvoices: false,
        lastFetchFailure: todayDateTime(),
      };
    }
    case InvoiceActionType.FAILURE_BACKOFF_ELAPSED:
      // Clearing the failure re-renders the consumers, whose getters enqueue the failed fetch again.
      return { ...state, lastFetchFailure: undefined };
    case InvoiceActionType.REFETCH:
      return {
        ...state,
        shouldRefetchInvoices: true,
      };
    case InvoiceActionType.ADD: {
      if (isNil(state.invoices) && isNil(state.unpaidRentInvoices)) {
        return state;
      }
      const entity = action.payload.invoice;
      const unpaidRentInvoices = updateUnpaidRentSnapshot(state.unpaidRentInvoices, entity);
      if (isNil(state.invoices) || state.invoices.some((item) => item.id === entity.id)) {
        return { ...state, unpaidRentInvoices };
      }
      return {
        ...state,
        unpaidRentInvoices,
        invoices: [...state.invoices, entity],
      };
    }
    case InvoiceActionType.UPDATE: {
      if (isNil(state.invoices) && isNil(state.unpaidRentInvoices)) {
        return state;
      }
      const updatedInvoice = action.payload.invoice;
      const unpaidRentInvoices = updateUnpaidRentSnapshot(state.unpaidRentInvoices, updatedInvoice);
      if (isNil(state.invoices)) {
        return { ...state, unpaidRentInvoices };
      }
      const newValidatingIds = updatedInvoice.invoiceNumber
        ? state.validatingInvoiceIds.filter((id) => id !== updatedInvoice.id)
        : state.validatingInvoiceIds;
      return {
        ...state,
        unpaidRentInvoices,
        validatingInvoiceIds: newValidatingIds,
        invoices: state.invoices.map((item) => (item.id === updatedInvoice.id ? updatedInvoice : item)),
      };
    }
    case InvoiceActionType.UPDATE_STATUS: {
      if (isNil(state.invoices) && isNil(state.unpaidRentInvoices)) {
        return state;
      }
      const { id: invoiceIdToUpdate, status } = action.payload;
      const unpaidRentInvoices = isNil(state.unpaidRentInvoices)
        ? null
        : state.unpaidRentInvoices.map((item) => (item.id === invoiceIdToUpdate ? { ...item, status } : item));
      if (isNil(state.invoices)) {
        return { ...state, unpaidRentInvoices };
      }
      return {
        ...state,
        unpaidRentInvoices,
        invoices: state.invoices.map((item) => (item.id === invoiceIdToUpdate ? { ...item, status } : item)),
      };
    }
    case InvoiceActionType.SET_VALIDATING:
      return {
        ...state,
        validatingInvoiceIds: [...state.validatingInvoiceIds, action.payload.invoiceId],
      };
    case InvoiceActionType.UNSET_VALIDATING:
      return {
        ...state,
        validatingInvoiceIds: state.validatingInvoiceIds.filter((id) => id !== action.payload.invoiceId),
      };
    case InvoiceActionType.DELETE: {
      if (isNil(state.invoices) && isNil(state.unpaidRentInvoices)) {
        return state;
      }
      const deletedInvoiceId = action.payload.id;
      const unpaidRentInvoices = isNil(state.unpaidRentInvoices)
        ? null
        : state.unpaidRentInvoices.filter((item) => item.id !== deletedInvoiceId);
      if (isNil(state.invoices)) {
        return { ...state, unpaidRentInvoices };
      }
      return {
        ...state,
        unpaidRentInvoices,
        companyInvoiceIdsDeletedDuringFetch: state.shouldFetchCompany
          ? [...state.companyInvoiceIdsDeletedDuringFetch, deletedInvoiceId]
          : state.companyInvoiceIdsDeletedDuringFetch,
        invoices: state.invoices.filter((item) => item.id !== deletedInvoiceId),
      };
    }
    default:
      return state;
  }
};

const getIndexNameFromFetchBy = (
  by: 'byClientId' | 'byLease' | 'byStatement' | 'byType' | 'byId' | 'byOwnerId'
): string => {
  switch (by) {
    case 'byClientId':
      return 'clientId';
    case 'byStatement':
      return 'statementId';
    case 'byType':
      return 'type';
    case 'byId':
      return 'id';
    case 'byOwnerId':
      return 'ownerId';
    case 'byLease':
    default:
      return 'leaseId';
  }
};

export const fetchInvoices = async (
  by: 'byClientId' | 'byLease' | 'byStatement' | 'byType' | 'byId' | 'byOwnerId',
  byValue: string,
  additionalFilter?: LooseObject,
  invoicesQuery: string = addInvoiceSentVersionChannels(addInvoiceIntegrationsProperties(syncInvoices))
): Promise<Invoice[]> => {
  const indexName = getIndexNameFromFetchBy(by);
  return await list<Invoice>(invoicesQuery, indexName, byValue, additionalFilter);
};

export const fetchInvoiceType = async (invoiceId: string): Promise<Pick<Invoice, 'id' | 'type'> | undefined> =>
  (await get<Pick<Invoice, 'id' | 'type'>>(getInvoiceType, invoiceId)) ?? undefined;

export const fetchInvoicesForFolders = async (by: FetchByType, byValue: string): Promise<Invoice[]> =>
  await list<Invoice>(syncInvoicesForFolder, by, byValue);

export const fetchInvoicesForLatePaymentFilter = async (tableClientId: string): Promise<Invoice[]> =>
  await list<Invoice>(listInvoicesForLatePaymentFilter, 'clientId', tableClientId, {
    and: [{ paid: { eq: false } }, { type: { eq: InvoiceType.RENT } }],
  });

export const InvoiceContext = createContext<InvoiceContext | null>(null);

interface InvoiceContextProviderProps {
  children?: ReactNode;
}

export const InvoiceContextProvider = ({ children }: InvoiceContextProviderProps) => {
  const { leasesCreationDelete } = usePermissions();
  const { clientId, rootUser } = useUser();
  const { leases, leasesLoading } = useLeases();
  const { loading: contextLoaderLoading } = useContextLoader();
  const leasesReady = !leasesLoading && !contextLoaderLoading;
  const { deletePosting } = usePostings();
  const [state, dispatch] = useReducer<Reducer<State, Action>>(contextReducer, initialState);
  const companyFetchScheduled = useRef<boolean>(false);
  const unpaidRentFetchScheduled = useRef<boolean>(false);

  useEffect(() => {
    if (isNil(state.lastFetchFailure)) {
      return undefined;
    }
    const retryTimeout = setTimeout(
      () => dispatch({ type: InvoiceActionType.FAILURE_BACKOFF_ELAPSED }),
      MILLISECONDS_BEFORE_RETRYING_A_FAILED_FETCH
    );
    return () => clearTimeout(retryTimeout);
  }, [state.lastFetchFailure]);

  const companyInvoices = useMemo<Invoice[]>(
    () =>
      state.error || !state.invoices ? [] : state.invoices.filter((invoice) => invoice.type === InvoiceType.COMPANY),
    [state.error, state.invoices]
  );

  useEffect(() => {
    let unmounted = false;
    const fetchAndSet = async () => {
      try {
        const result = await fetchInvoices('byType', InvoiceType.STATEMENT);
        const resultFiltered = rootUser ? result : result.filter((li) => li.lease && isIdInList(li.lease, leases));
        if (!unmounted) {
          dispatch({ type: InvoiceActionType.FETCHED, payload: { invoices: resultFiltered } });
        }
      } catch (error) {
        console.error('[invoices] fetching tenant statement invoices failed', error);
        if (!unmounted) {
          dispatch({ type: InvoiceActionType.FETCH_FAILED, payload: {} });
        }
      }
    };
    if (state.shouldFetchTenantStatements && leasesReady) {
      fetchAndSet();
    }
    return () => {
      unmounted = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.shouldFetchTenantStatements, leasesReady]);

  useEffect(() => {
    let unmounted = false;
    const fetchAndSet = async () => {
      const leaseId = state.shouldFetchLeases[0];
      if (isEmpty(leaseId)) {
        return;
      }
      try {
        const fetchStartedAt = todayDateTime();
        const result = await fetchInvoices('byLease', leaseId);
        const resultFiltered = rootUser ? result : result.filter((li) => li.lease && isIdInList(li.lease, leases));
        if (!unmounted) {
          dispatch({ type: InvoiceActionType.FETCHED, payload: { invoices: resultFiltered, leaseId, fetchStartedAt } });
        }
      } catch (error) {
        console.error('[invoices] fetching lease invoices failed', error);
        if (!unmounted) {
          dispatch({ type: InvoiceActionType.FETCH_FAILED, payload: { leaseId } });
        }
      }
    };
    if (state.shouldFetchLeases && leasesReady) {
      fetchAndSet();
    }
    return () => {
      unmounted = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.shouldFetchLeases, leasesReady]);

  useEffect(() => {
    let unmounted = false;
    const fetchAndSet = async () => {
      if (isNilOrEmpty(clientId)) {
        return;
      }
      try {
        const result = await fetchInvoices('byClientId', getTableClientId(clientId, INVOICE_TABLE_NAME));
        const resultFiltered = rootUser
          ? result
          : result.filter((invoice) => invoice.lease && isIdInList(invoice.lease, leases));
        if (!unmounted) {
          dispatch({ type: InvoiceActionType.FETCHED, payload: { invoices: resultFiltered } });
        }
      } catch (error) {
        console.error('[invoices] refetching client invoices failed', error);
        if (!unmounted) {
          dispatch({ type: InvoiceActionType.FETCH_FAILED, payload: {} });
        }
      }
    };
    if (state.shouldRefetchInvoices && leasesReady) {
      fetchAndSet();
    }
    return () => {
      unmounted = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.shouldRefetchInvoices, leasesReady]);

  useEffect(() => {
    let unmounted = false;
    const fetchAndSet = async () => {
      const ownerId = state.shouldFetchOwners[0];
      if (isEmpty(ownerId)) {
        return;
      }
      try {
        const result = await fetchInvoices('byOwnerId', ownerId);
        const resultFiltered = rootUser ? result : result.filter((li) => li.lease && isIdInList(li.lease, leases));
        if (!unmounted) {
          dispatch({ type: InvoiceActionType.FETCHED, payload: { invoices: resultFiltered, ownerId: ownerId } });
        }
      } catch (error) {
        console.error('[invoices] fetching owner invoices failed', error);
        if (!unmounted) {
          dispatch({ type: InvoiceActionType.FETCH_FAILED, payload: { ownerId } });
        }
      }
    };
    if (state.shouldFetchOwners && leasesReady) {
      fetchAndSet();
    }
    return () => {
      unmounted = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.shouldFetchOwners, leasesReady]);

  useEffect(() => {
    let unmounted = false;
    const fetchAndSet = async () => {
      try {
        const result = await fetchInvoicesForLatePaymentFilter(getTableClientId(clientId!, INVOICE_TABLE_NAME));
        if (!unmounted) {
          dispatch({ type: InvoiceActionType.FETCHED_UNPAID_RENT_INVOICES, payload: { invoices: result } });
        }
      } catch (error) {
        if (!unmounted) {
          dispatch({
            type: InvoiceActionType.FETCH_UNPAID_RENT_INVOICES_ERROR,
            payload: { error: (error as Error).message },
          });
        }
      }
    };
    if (state.shouldFetchUnpaidRentInvoices && !isNil(clientId)) {
      fetchAndSet();
    }
    return () => {
      unmounted = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.shouldFetchUnpaidRentInvoices, clientId]);

  useEffect(() => {
    let unmounted = false;
    const fetchAndSet = async () => {
      try {
        const result = await fetchInvoices('byType', InvoiceType.COMPANY);
        if (!unmounted) {
          dispatch({ type: InvoiceActionType.FETCHED_COMPANY, payload: { invoices: result } });
        }
      } catch (error) {
        if (!unmounted) {
          dispatch({
            type: InvoiceActionType.FETCH_COMPANY_ERROR,
            payload: { error: (error as Error)?.message ?? String(error) },
          });
        }
      }
    };
    if (state.shouldFetchCompany) {
      fetchAndSet();
    }
    return () => {
      unmounted = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.shouldFetchCompany]);

  useEffect(() => {
    if (!state.shouldFetchCompany && state.companyRefetchPending) {
      dispatch({ type: InvoiceActionType.FETCH_COMPANY, payload: { force: true } });
    }
  }, [state.shouldFetchCompany, state.companyRefetchPending]);

  useSubscriptions<OnCreateInvoiceSubscription, OnUpdateInvoiceSubscription, OnDeleteInvoiceSubscription>(
    onCreateInvoice,
    onUpdateInvoice,
    onDeleteInvoice,
    (data) => {
      dispatch({
        type: InvoiceActionType.ADD,
        payload: { invoice: data.onCreateInvoice as Invoice },
      });
    },
    (data) => {
      dispatch({
        type: InvoiceActionType.UPDATE,
        payload: { invoice: data.onUpdateInvoice as Invoice },
      });
    },
    (data) => {
      const { id } = data.onDeleteInvoice as Invoice;
      dispatch({
        type: InvoiceActionType.DELETE,
        payload: { id },
      });
    }
  );

  useSubscriptions<
    OnCreateCustomSubscriptionSubscription,
    OnUpdateCustomSubscriptionSubscription,
    OnDeleteCustomSubscriptionSubscription
  >(
    onCreateCustomSubscription,
    onUpdateCustomSubscription,
    onDeleteCustomSubscription,
    (data) => {
      const newData = data.onCreateCustomSubscription as CustomSubscription;
      if (newData.name === CustomSubscriptionType.REFETCH && clientId) {
        const typedValue = getTypedCustomSubscriptionValueForRefetch(newData);
        if (typedValue.table !== INVOICE_TABLE_NAME) {
          return;
        }
        if (typedValue.leaseId) {
          dispatch({
            type: InvoiceActionType.FETCH_LEASE,
            payload: { leaseId: typedValue.leaseId, force: true },
          });
          return;
        }
        if (typedValue.isCompanyInvoice) {
          dispatch({ type: InvoiceActionType.FETCH_COMPANY, payload: { force: true } });
          return;
        }
        dispatch({ type: InvoiceActionType.REFETCH });
      }
    },
    () => {},
    () => {}
  );

  useSubscriptions<OnCreatePostingSubscription, OnUpdatePostingSubscription, OnDeletePostingSubscription>(
    onCreatePosting,
    onUpdatePosting,
    onDeletePosting,
    (data) => {
      const newData = data.onCreatePosting as Posting;

      if (newData.leaseId && newData.invoiceId)
        dispatch({ type: InvoiceActionType.FETCH_LEASE, payload: { leaseId: newData.leaseId, force: true } });
    },
    (data) => {
      const newData = data.onUpdatePosting as Posting;
      if (newData.leaseId && newData.invoiceId)
        dispatch({ type: InvoiceActionType.FETCH_LEASE, payload: { leaseId: newData.leaseId, force: true } });
    },
    (data) => {
      const newData = data.onDeletePosting as Posting;
      if (newData.leaseId && newData.invoiceId)
        dispatch({ type: InvoiceActionType.FETCH_LEASE, payload: { leaseId: newData.leaseId, force: true } });
    }
  );

  const createInvoice = async (
    input: CreateInvoiceInput | Omit<CreateInvoiceInput, 'clientId' | 'readId'>
  ): Promise<Invoice> => {
    const invoice = await mutation<Invoice, CreateInvoiceMutationVariables>(createMutation, {
      input: {
        ...(cleanInputCreate(input) as CreateInvoiceInput),
        clientId: getTableClientId(clientId!, 'Invoice'),
        readId: getReadId(clientId!, 'Invoice'),
      },
    });
    return invoice;
  };

  const resendInvoiceToPeppol = async (invoiceId: string): Promise<void> => {
    const result = await mutation<StatusResult, ResendInvoiceToPeppolMutationVariables>(resendInvoiceToPeppolMutation, {
      input: { invoiceId },
    });
    if (!result?.success) {
      throw new Error(result?.message ?? `Invoice ${invoiceId} was not queued for Peppol`);
    }
    dispatch({ type: InvoiceActionType.REFETCH });
  };

  const chargeInvoiceByMandate = async (invoiceId: string): Promise<void> => {
    await mutation<ChargeInvoiceByMandateMutation, ChargeInvoiceByMandateMutationVariables>(
      chargeInvoiceByMandateMutation,
      {
        input: { invoiceId, clientId: clientId! },
      }
    );
    dispatch({ type: InvoiceActionType.REFETCH });
  };

  const deferLeaseInvoicesFetchOutOfRender = (leaseId: string) => {
    setTimeout(() => dispatch({ type: InvoiceActionType.FETCH_LEASE, payload: { leaseId } }), 0);
  };

  const getInvoicesOfLease = (leaseId: string) => {
    deferLeaseInvoicesFetchOutOfRender(leaseId);
    if (state.error || !state.invoices) {
      return [];
    }
    return state.invoices.filter((invoice) => invoice.lease && invoice.lease.id === leaseId);
  };

  const getUnpaidInvoicesOfLease = (leaseId: string) => {
    deferLeaseInvoicesFetchOutOfRender(leaseId);
    if (state.loading || state.error || !state.invoices) {
      return [];
    }

    return state.invoices.filter((invoice) => invoice.lease && invoice.lease.id === leaseId && !invoice.paid);
  };

  const getTenantStatementsInvoices = () => {
    dispatch({ type: InvoiceActionType.FETCH_TENANT_STATEMENTS });
    if (state.loading || state.error || !state.invoices) {
      return [];
    }

    return state.invoices.filter(
      (invoice) => invoice.type === InvoiceType.STATEMENT && invoice.status === InvoiceStatus.INVOICED
    );
  };

  const getCompanyInvoices = (): Invoice[] => {
    const alreadyHandled =
      state.shouldFetchCompany || state.companyInvoicesFetched || !isNil(state.companyInvoicesError);
    if (!alreadyHandled && !companyFetchScheduled.current) {
      companyFetchScheduled.current = true;
      queueMicrotask(() => {
        companyFetchScheduled.current = false;
        dispatch({ type: InvoiceActionType.FETCH_COMPANY, payload: { force: false } });
      });
    }
    return companyInvoices;
  };

  const getUnpaidRentInvoices = (): Invoice[] => {
    const alreadyHandled =
      state.shouldFetchUnpaidRentInvoices || state.unpaidRentInvoicesFetched || !isNil(state.unpaidRentInvoicesError);
    if (!alreadyHandled && !unpaidRentFetchScheduled.current) {
      unpaidRentFetchScheduled.current = true;
      queueMicrotask(() => {
        unpaidRentFetchScheduled.current = false;
        dispatch({ type: InvoiceActionType.FETCH_UNPAID_RENT_INVOICES });
      });
    }
    return state.unpaidRentInvoices ?? [];
  };

  const refetchCompanyInvoices = (): void => {
    dispatch({ type: InvoiceActionType.FETCH_COMPANY, payload: { force: true } });
  };

  const getInvoicesOfOwner = async (ownerId?: string) => {
    dispatch({ type: InvoiceActionType.FETCH_OWNER, payload: { ownerId } });
    if (state.loading || state.error || !state.invoices) {
      return [];
    }

    return state.invoices.filter((invoice) => invoice.owner && invoice.owner.id === ownerId);
  };

  const updateInvoice = async (updates: UpdateInvoiceInput): Promise<Invoice> =>
    mutation<Invoice>(updateMutation, { input: cleanInputUpdate(updates, false, true) });

  const refetchIfCompanyInvoice = (invoiceId: string): void => {
    const invoice = (state.invoices ?? []).find((currentInvoice) => currentInvoice.id === invoiceId);
    if (invoice?.type === InvoiceType.COMPANY) {
      refetchCompanyInvoices();
    }
  };

  // The backend's refetch signal is best-effort: a lifecycle change must not depend on it to reach the list
  const refetchLeaseOfInvoice = (invoiceId: string, knownLeaseId?: string | null): void => {
    const leaseId =
      knownLeaseId ?? (state.invoices ?? []).find((currentInvoice) => currentInvoice.id === invoiceId)?.leaseId;
    if (!isNil(leaseId)) {
      dispatch({ type: InvoiceActionType.FETCH_LEASE, payload: { leaseId, force: true } });
    }
  };

  const openInvoiceDispute = async (input: OpenInvoiceDisputeInput, leaseId?: string | null): Promise<boolean> => {
    try {
      await mutation<MutationStatus, OpenInvoiceDisputeMutationVariables>(openInvoiceDisputeMutation, { input });
      refetchLeaseOfInvoice(input.invoiceId, leaseId);
      return true;
    } catch (error) {
      console.error('[invoices] opening the dispute of the invoice failed', error);
      return false;
    }
  };

  const liftInvoiceDispute = async (input: LiftInvoiceDisputeInput, leaseId?: string | null): Promise<boolean> => {
    try {
      await mutation<MutationStatus, LiftInvoiceDisputeMutationVariables>(liftInvoiceDisputeMutation, { input });
      refetchLeaseOfInvoice(input.invoiceId, leaseId);
      return true;
    } catch (error) {
      console.error('[invoices] lifting the dispute of the invoice failed', error);
      return false;
    }
  };

  const deleteInvoice = async (invoice: Invoice): Promise<Invoice | null> => {
    if (invoice.type === InvoiceType.COMPANY) {
      await deleteDraftInvoice(invoice.id);
      return invoice;
    }
    if (!leasesCreationDelete) {
      return null;
    }

    const postingsToDelete = await fetchPostings('byInvoice', invoice.id, undefined, undefined, {
      includesDraftPostings: true,
    });
    const result = await deleteEntityWithFetchBefore<Invoice, DeleteInvoiceMutationVariables>(
      invoice,
      getQuery,
      deleteMutation
    );

    const deletedPostingsPromises = postingsToDelete.map((postingToDelete) => deletePosting(postingToDelete));
    await Promise.all(deletedPostingsPromises);
    return result;
  };

  const deleteDraftInvoice = async (invoiceId: string): Promise<void> => {
    await mutation<MutationStatus, DeleteDraftInvoiceMutationVariables>(deleteDraftInvoiceMutation, {
      input: { invoiceId },
    });
    dispatch({ type: InvoiceActionType.DELETE, payload: { id: invoiceId } });
  };

  const validateInvoice = async (
    invoiceId: string,
    invoiceNumber?: string | null,
    renumber?: boolean
  ): Promise<MutationStatus> => {
    dispatch({ type: InvoiceActionType.SET_VALIDATING, payload: { invoiceId } });
    const result = await mutation<MutationStatus, ValidateInvoiceMutationVariables>(validateInvoiceMutation, {
      input: { invoiceId, invoiceNumber, renumber },
    }).catch((error: Error) => {
      dispatch({ type: InvoiceActionType.UNSET_VALIDATING, payload: { invoiceId } });
      throw error;
    });
    const validatedInvoice = (state.invoices ?? []).find((invoice) => invoice.id === invoiceId);
    if (validatedInvoice?.type === InvoiceType.COMPANY) {
      dispatch({ type: InvoiceActionType.UPDATE_STATUS, payload: { id: invoiceId, status: InvoiceStatus.POSTED } });
    }
    const reversedInvoiceId =
      validatedInvoice?.type === InvoiceType.COMPANY && validatedInvoice.creditNote
        ? validatedInvoice.reversedInvoiceId
        : null;
    if (!isNil(reversedInvoiceId)) {
      dispatch({
        type: InvoiceActionType.UPDATE_STATUS,
        payload: { id: reversedInvoiceId, status: InvoiceStatus.REVERSED },
      });
    }
    refetchIfCompanyInvoice(invoiceId);
    refetchLeaseOfInvoice(invoiceId);
    return result;
  };

  const reverseCompanyInvoice = async (invoiceId: string): Promise<void> => {
    await mutation<MutationStatus, ReverseCompanyInvoiceMutationVariables>(reverseCompanyInvoiceMutation, {
      input: { invoiceId, readId: getReadId(clientId!, 'Invoice'), clientId: clientId! },
    });
    refetchCompanyInvoices();
  };

  const resetInvoiceToDraft = async (invoiceId: string, reason?: string | null): Promise<void> => {
    try {
      await mutation<MutationStatus, ResetInvoiceToDraftMutationVariables>(resetInvoiceToDraftMutation, {
        input: { invoiceId, reason },
      });
    } finally {
      refetchIfCompanyInvoice(invoiceId);
      refetchLeaseOfInvoice(invoiceId);
    }
  };

  const sendInvoice = async (
    invoiceId: string,
    sendMethods?: CompanyInvoiceSendMethod[],
    note?: string
  ): Promise<SendInvoiceResult> => {
    const result = await mutation<MutationStatus, SendInvoiceMutationVariables>(sendInvoiceMutation, {
      input: { invoiceId, sendMethods, note },
    });
    refetchIfCompanyInvoice(invoiceId);
    const hasEmailCommunication = isNil(sendMethods)
      ? result.id !== invoiceId
      : sendMethods.includes(CompanyInvoiceSendMethod.EMAIL);
    if (!hasEmailCommunication) {
      return { awaitingManualSend: false };
    }
    const communication = await fetchCommunication(result.id).catch(() => undefined);
    return {
      communicationId: result.id,
      awaitingManualSend: isNil(communication) || isCommunicationAwaitingManualSend(communication),
    };
  };

  // The caller opens the copy by id right away: wait until the eventually consistent reads return it with its lines
  const waitUntilDuplicateIsReadable = async (sourceInvoiceId: string, duplicateId: string): Promise<void> => {
    await fetchUntil(
      () => fetchInvoices('byId', duplicateId),
      (invoices) => !isEmpty(invoices)
    );
    const sourceInvoice = (state.invoices ?? []).find((invoice) => invoice.id === sourceInvoiceId);
    if (!isNil(sourceInvoice) && sourceInvoice.type !== InvoiceType.COMPANY) {
      await fetchUntil(
        () => fetchPostings('byInvoice', duplicateId, undefined, undefined, { includesDraftPostings: true }),
        (postings) => areManualDocumentPostingsReadable(sourceInvoice, postings)
      );
    }
  };

  const duplicateCompanyInvoice = async (invoiceId: string): Promise<string> => {
    const result = await retryWhileDocumentNotReady(() =>
      mutation<MutationStatus, DuplicateCompanyInvoiceMutationVariables>(duplicateCompanyInvoiceMutation, {
        input: { invoiceId },
      })
    );
    if (isNilOrEmpty(result?.id)) {
      throw new Error('Duplicate company invoice did not return an invoice id');
    }
    // The copy is committed: a failed read must not report the duplication as failed, a retry would copy it again
    await waitUntilDuplicateIsReadable(invoiceId, result.id).catch(() => undefined);
    refetchIfCompanyInvoice(invoiceId);
    refetchLeaseOfInvoice(invoiceId);
    return result.id;
  };

  const values = useMemo(
    () => ({
      ...state,
      createInvoice,
      resendInvoiceToPeppol,
      chargeInvoiceByMandate,
      getInvoicesOfLease,
      getInvoicesOfOwner,
      getCompanyInvoices,
      refetchCompanyInvoices,
      getUnpaidRentInvoices,
      getUnpaidInvoicesOfLease,
      getTenantStatementsInvoices,
      updateInvoice,
      openInvoiceDispute,
      liftInvoiceDispute,
      deleteInvoice,
      deleteDraftInvoice,
      validateInvoice,
      duplicateCompanyInvoice,
      reverseCompanyInvoice,
      resetInvoiceToDraft,
      sendInvoice,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state]
  );

  return <InvoiceContext.Provider value={values}>{children}</InvoiceContext.Provider>;
};

export const useInvoices = (): InvoiceContext => {
  const context = useContext<InvoiceContext | null>(InvoiceContext);

  if (!context) {
    throw new Error('`useInvoices` hook must be used within a `InvoiceContext` component');
  }

  return context;
};
